import { Alert, Box, LinearProgress, Stack } from "@mui/material";

import { InsightsErrorState } from "../../components/Insights/InsightsAsyncState";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";
import AggregateResultsHeader from "../pricing-reports/AggregateResultsHeader";
import { statisticCardContainerSx } from "../pricing-reports/constants";
import VerifyPriceAnomalyBanner from "./VerifyPriceAnomalyBanner";
import VerifyPriceAnomalyButton from "./VerifyPriceAnomalyButton";
import VerifyPriceCardGrid from "./VerifyPriceCardGrid";
import VerifyPriceFilters from "./VerifyPriceFilters";
import VerifyPriceReviewDialog from "./VerifyPriceReviewDialog";
import VerifyPriceSortSelect from "./VerifyPriceSortSelect";
import VerifyPriceSummaryCards from "./VerifyPriceSummaryCards";

export default function VerifyPricePanelContent({ panelState }) {
  const {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    provinceChoices,
    selectedProvince,
    setSelectedProvince,
    productFilterChoices,
    selectedProducts,
    setSelectedProducts,
    retailerChoices,
    selectedRetailers,
    setSelectedRetailers,
    providerChoices,
    selectedProvidersInclude,
    setSelectedProvidersInclude,
    humanVerifiedView,
    setHumanVerifiedView,
    sort,
    setSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    displayItems,
    totalCount,
    counts,
    isCountsLoading,
    isLoadingFilterOptions,
    isPublishedProductsLoading,
    pageInitialLoading,
    pageRefreshing,
    anyFetchError,
    selectedItem,
    dialogOpen,
    panelMode,
    setPanelMode,
    actionError,
    actionLoading,
    unitChoices,
    resolveProvinceForApi,
    formatProductOptionLabel,
    formatProviderOptionLabel,
    handleCardClick,
    handleCloseDialog,
    handleAccept,
    handleReject,
    handleRestore,
    handleSkip,
    handleSaveRecord,
    anomalyMode,
    anomalyLoading,
    anomalyNotice,
    handleFindAnomalies,
  } = panelState;

  return (
    <Stack spacing={2}>
      <Box className={insightsSectionClassName("insights-section--filter")}>
        <VerifyPriceFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          provinceChoices={provinceChoices}
          selectedProvince={selectedProvince}
          setSelectedProvince={setSelectedProvince}
          productFilterChoices={productFilterChoices}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
          retailerChoices={retailerChoices}
          selectedRetailers={selectedRetailers}
          setSelectedRetailers={setSelectedRetailers}
          providerChoices={providerChoices}
          selectedProvidersInclude={selectedProvidersInclude}
          setSelectedProvidersInclude={setSelectedProvidersInclude}
          isLoadingFilterOptions={isLoadingFilterOptions}
          isPublishedProductsLoading={isPublishedProductsLoading}
          formatProductOptionLabel={formatProductOptionLabel}
          formatProviderOptionLabel={formatProviderOptionLabel}
          humanVerifiedView={humanVerifiedView}
          setHumanVerifiedView={setHumanVerifiedView}
        />
      </Box>

      <Box className={insightsSectionClassName("insights-section--content")}>
        <Stack spacing={2}>
          {anyFetchError ? (
            <InsightsErrorState
              message={anyFetchError}
              fallbackMessage="Something went wrong while loading price records."
            />
          ) : null}

          <VerifyPriceSummaryCards counts={counts} isLoading={isCountsLoading} />

          <Box sx={{ p: 2, ...statisticCardContainerSx }}>
            <Stack spacing={1.5}>
              <AggregateResultsHeader
                title="Price history records"
                subtitle={
                  humanVerifiedView === "voided"
                    ? "Voided records for the current filters. Restore a card if it was rejected by mistake."
                    : "Individual price observations for the current filters. Accept or reject on a card, or open it to review."
                }
                headerActions={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <VerifyPriceSortSelect value={sort} onChange={setSort} />
                    {humanVerifiedView === "voided" ? null : (
                      <VerifyPriceAnomalyButton
                        active={anomalyMode}
                        loading={anomalyLoading}
                        onClick={handleFindAnomalies}
                      />
                    )}
                  </Stack>
                }
              />
              {anomalyLoading ? <LinearProgress aria-label="Scanning for price anomalies" /> : null}
              <VerifyPriceAnomalyBanner notice={anomalyNotice} />
              {actionError && !dialogOpen ? <Alert severity="error">{actionError}</Alert> : null}
              <VerifyPriceCardGrid
                items={displayItems}
                totalCount={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={setPage}
                onRowsPerPageChange={(nextSize) => {
                  setRowsPerPage(nextSize);
                  setPage(0);
                }}
                isLoading={pageInitialLoading && !anomalyMode}
                isRefreshing={pageRefreshing && !anomalyMode}
                onCardClick={handleCardClick}
                onAccept={handleAccept}
                onReject={handleReject}
                onRestore={handleRestore}
                actionLoading={actionLoading}
                emptyMessage={
                  anomalyMode
                    ? "No remaining anomalies for this scan."
                    : humanVerifiedView === "voided"
                      ? "No voided records for the current filters."
                      : "No price records for the current filters."
                }
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <VerifyPriceReviewDialog
        open={dialogOpen}
        item={selectedItem}
        panelMode={panelMode}
        onPanelModeChange={setPanelMode}
        actionLoading={actionLoading}
        actionError={actionError}
        onClose={handleCloseDialog}
        onAccept={handleAccept}
        onReject={handleReject}
        onRestore={handleRestore}
        onSkip={handleSkip}
        onSaveRecord={handleSaveRecord}
        productFilterChoices={productFilterChoices}
        retailerChoices={retailerChoices}
        provinceChoices={provinceChoices}
        unitChoices={unitChoices}
        formatProductOptionLabel={formatProductOptionLabel}
        isPublishedProductsLoading={isPublishedProductsLoading}
        isLoadingFilterOptions={isLoadingFilterOptions}
        resolveProvinceForApi={resolveProvinceForApi}
      />
    </Stack>
  );
}
